import { AnnotationIconColorType, AnnotationIconType, StatusCategory } from '@/api/storymapCardDetails/queries';
import { ToolIntegrationToolType } from '@/pages/main/storymap/store/types';
import { StorymapCollaborator } from '../../types';

export class StorymapCardDetailsState {
    card: StorymapCard = null;

    attachments: CardAttachmentModel[] = [];
    webLinks: CardWeblinkModel[] = [];

    comments: CardCommentModel[] = [];

    cardPersonas: CardPersonaModel[] = [];
    personas: Persona[] = [];

    cardAnnotations: CardAnnotationModel[] = [];

    mentionableUsers: MentionableUser[] = [];

    statuses: SubtaskStatus[] = [];

    annotations: Annotation[] = [];

    collaborators: StorymapCollaborator[] = [];

    taskName: string = '';
    activityName: string = '';
    canComment: boolean = false;
    canReadComments: boolean = false;
    hasDescription: boolean = false;
    isCardLinkedToIntegrationTool: boolean = false;
    isMissingImpersonationToken: boolean = false;
    canBeLinkedToIntegrationTool: boolean = false;
    linkedUrl: string = '';
    integrationToolType: ToolIntegrationToolType = ToolIntegrationToolType.NotIntegrated;
    isCardKeyEnabled: boolean = false;
    feedbacks: Feedback[] = [];

    isInitialized: boolean = false;

    // Incremented by every card-details preload; a card-details load is current only while it is unchanged (R16/R17).
    loadSeq: number = 0;
}

export interface LinkedCard {
    id: string;
    title: string;
    storymapTitle: string;
    color: number;
    key: string;
    linkedItemKey: string;
    linkedItemTypeId?: string;
    linkedItemTypeName?: string;
    isCardKeyEnabled: boolean;
    cardType: CardType;
    isTrashed: boolean;
    integrationToolType: ToolIntegrationToolType;
    isLinkedToIntegrationTool: boolean;
    isExternal: boolean;
}

export interface Persona {
    id: string;
    name: string;
    picture: string;
    group: string;
    isGlobal: boolean;
}

export const getCardType = (card: StorymapCard) => {
    return card.isActivity
        ? CardType.Activity
        : card.isTask
        ? CardType.Task
        : card.isSubtask
        ? CardType.Subtask
        : CardType.UnscheduledSubtask;
};

export const getCardTypeName = (card: StorymapCard) => {
    switch (getCardType(card)) {
        case CardType.Activity:
            return 'Activity';
        case CardType.Task:
            return 'Task';
        case CardType.Subtask:
            return 'Subtask';
        case CardType.UnscheduledSubtask:
            return 'UnscheduledSubtask';
    }
};

export enum CardType {
    Unknown,
    Activity,
    Task,
    Subtask,
    UnscheduledSubtask,
}

export interface StorymapCard {
    id: string;
    key: string;
    storymapId: string;
    description: string;
    hasDescription: boolean;
    name: string;
    storymapSlug: string;
    isSubtask: boolean;
    isTask: boolean;
    isActivity: boolean;
    assigneeId?: string;
    assigneeDisplayName?: string;
    hasAssignee?: boolean;
    subtaskState?: number;
    estimation?: number;
    estimationUnit: string;
    createdAt: string;
    createdBy: string;
    linkedItemKey: string;
    linkedItemId: string;
    linkedItemTypeId: string;
    linkedItemTypeName: string;
    isWatched?: boolean;
}

export interface CardAttachmentModel {
    id: string;
    name: string;
    hasThumbnail: boolean;
    extension: string;
    uploadedBy: string;
    uploadedAt: string;
    fileSize: number;
    // Delivered to published recipients only.
    openUrl?: string;
    downloadUrl?: string;
    thumbnailUrl?: string;
}

interface CardCommentModel {
    id: string;
    createdBy: string;
    createdByUserId: string;
    timeToDisplay: string;
    isCurrentUsersComment: boolean;
    commentText: string;
    isEdited: boolean;
}

export interface CardWeblinkModel {
    id: string;
    url: string;
    name: string;
    faviconId: string;
    createdBy: string;
    createdAt?: string;
}

interface CardPersonaModel {
    personaId: string;
}

export interface CardAnnotationModel {
    annotationId: string;
}

interface MentionableUser {
    id: string;
    name: string;
    email: string;
    isCurrentUser: boolean;
}

export interface SubtaskStatus {
    key: number;
    name: string;
    color: string;
    category: StatusCategory;
    order: number;
}

export interface Annotation {
    id: string;
    icon: AnnotationIconType;
    emoji: string;
    isEmoji: boolean;
    color: AnnotationIconColorType;
    name: string;
    isArchived: boolean;
    order: number;
}

export interface Feedback {
    id: string;
    content: string;
    categoryId?: string;
    contactEmail?: string;
    creatorToolType: number;
    externalToolName?: string;
    isImportant: boolean;
    isArchived: boolean;
    attachmentCount: number;
    commentCount: number;
    tagIds?: string[];
    createdBy?: string;
    createdById?: string;
    createdAt: string;
}
